'use client';

import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  styled,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FilterIcon } from '@/primitives/icons/FilterIcon';
import { FilterPlusIcon } from '@/primitives/icons/FilterPlusIcon';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import { FieldOption, GetAnimalsRow } from '../constants';
import { alpha } from '@mui/material/styles';
import { green, slate } from '@/lib/colours';
import Link from 'next/link';
import MemberInlineDisplay from '../member/MemberInlineDisplay';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { pickPrimaryImageUrl } from '@/publicSearch/utils/pickPrimaryImageUrl';
import { useDocumentImageWithSociety } from '@/hooks/publicSearch/useDocumentImageWithSociety';
import ImageLightbox from '@/components/publicSearch/ImageLightbox';
import { SEARCH_TYPE } from '@/constants/searchType';
import { displayPlainText } from '@/utils/stripHtmlForTooltip';
import { useQuickFilterActions } from '@/hooks/publicSearch/useQuickFilterActions';
import { SocietyFormattedDate } from '@/components/publicSearch/SocietyFormattedDate';
import { AnimalSexBadge } from '@/hooks/publicSearch/animal/helpers';
import {
  ANIMAL_STATUS_TONE,
  getAnimalStatusTone,
} from '@/components/animals/animalStatus';
import { Cell, PropsHeader, PropsValue } from './cardStyles';
import { CardThumbnail } from './CardThumbnail';

const StyledCard = styled(Card)(({ theme }) => ({
  padding: '16px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: `0 0 8px ${alpha(theme.palette.common.black, 0.1)}`,
  border: `1px solid ${(theme.vars ?? theme).palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    borderRadius: 0,
    boxShadow: 'none',
    border: 'none',
    borderBottom: `1px solid ${(theme.vars ?? theme).palette.divider}`,
  },
}));

const StatusBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ isActive }) => ({
  borderRadius: '4px',
  padding: '4px 12px',
  width: 'fit-content',
  background: isActive ? green[100] : slate[50],
  color: isActive ? green[700] : slate[700],
  fontSize: '12px',
  lineHeight: '16px',
  textTransform: 'capitalize',
}));

const Name = styled(Link)(({ theme }) => ({
  display: '-webkit-box',
  lineClamp: 2,
  overflow: 'hidden',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  wordBreak: 'break-word',
  textOverflow: 'ellipsis',
  color: (theme.vars ?? theme).palette.info.main,
  fontSize: '18px',
  fontWeight: 500,
  textDecoration: 'none',
}));

const Grid = styled(CardContent)({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  columnGap: '16px',
  rowGap: '4px',
  padding: 0,
});

const Footer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  marginTop: '4px',
  minHeight: '44px',
});

const PropsLink = styled(Name)(({ theme }) => ({
  lineClamp: 1,
  WebkitLineClamp: 1,
  fontSize: '16px',
  lineHeight: '20px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    lineHeight: '18px',
  },
}));

const QuickFilterRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  width: '100%',
  minWidth: 0,
  '& .quick-filter-icon': {
    opacity: 0,
    flexShrink: 0,
  },
  '&:hover .quick-filter-icon': {
    opacity: 1,
  },
  [theme.breakpoints.down('sm')]: {
    '& .quick-filter-icon': {
      opacity: 1,
    },
  },
}));

function QuickFilterButton({
  field,
  filterValue,
  onClick,
}: {
  field: string;
  filterValue: string;
  onClick: (field: string, filterValue: string) => void;
}) {
  return (
    <Tooltip title="Set filter" placement="top">
      <IconButton
        size="small"
        className="quick-filter-icon"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick(field, filterValue);
        }}
        sx={{
          p: 0.5,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
        aria-label="Set filter"
      >
        <FilterIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function AddFilterButton({
  field,
  filterValue,
  onClick,
}: {
  field: string;
  filterValue: string;
  onClick: (field: string, filterValue: string) => void;
}) {
  return (
    <Tooltip title="Add filter" placement="top">
      <IconButton
        size="small"
        className="quick-filter-icon"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick(field, filterValue);
        }}
        sx={{
          p: 0.5,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
        aria-label="Add filter"
      >
        <FilterPlusIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

export default function AnimalCard(props: GetAnimalsRow) {
  const {
    ID: id,
    ANIMAL_NAME: rawName,
    ANIMAL_STATUS: status,
    ANIMAL_IDENT: identifier,
    BREEDER: breeder,
    COLOUR: color,
    CURRENT_OWNER: owner,
    DAM: dam,
    DATE_OF_BIRTH: birthDate,
    REGISTRATION_STATUS: registrationStatus,
    SEX: sex,
    SIRE: sire,
    SALE_COUNT: saleCount = 0,
  } = props;
  const name = displayPlainText(rawName);
  const displayName = name || identifier || '';
  const society = usePublicSearchContext((context) => context.society);
  const orgCode = society.societyCode ?? '';
  const societyId =
    society.societyId ?? (society.orgId ? Number(society.orgId) : undefined);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const primaryImg = pickPrimaryImageUrl(
    props.documents?.map((doc) => ({
      ...doc,
      isPrimary: doc.isPrimary ?? false,
      isDeleted: doc.isDeleted ?? false,
    })),
  );
  const { imgSrc: displayImg } = useDocumentImageWithSociety(
    primaryImg,
    societyId,
  );
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
  const { applyQuickFilter: handleQuickFilter, upsertFilter: handleAddFilter } =
    useQuickFilterActions(SEARCH_TYPE.ANIMAL);
  const statusTone = getAnimalStatusTone(status);
  const registrationStatusLabel = displayPlainText(registrationStatus);

  return (
    <StyledCard>
      <Box
        sx={{
          display: 'flex',
        }}
      >
        <Box>
          <Box
            sx={{
              minHeight: '24px',
              mb: '8px',
              display: 'flex',
              gap: 0.5,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {status && (
              <StatusBadge isActive={statusTone === ANIMAL_STATUS_TONE.SUCCESS}>
                {status.toLowerCase()}
              </StatusBadge>
            )}
            {registrationStatusLabel && (
              <StatusBadge isActive sx={{ textTransform: 'none' }}>
                {registrationStatusLabel}
              </StatusBadge>
            )}
            {saleCount > 0 && (
              <Tooltip
                title={`${saleCount} active listing${saleCount === 1 ? '' : 's'}`}
              >
                <StatusBadge isActive>On sale</StatusBadge>
              </Tooltip>
            )}
          </Box>
          <Box
            sx={{
              height: '48px',
            }}
          >
            {id && displayName && orgCode && (
              <Tooltip disableInteractive title="View Details">
                <Name href={`/${orgCode}/${ROUTE_SEGMENT.ANIMALS}/${id}`}>
                  {displayName}
                </Name>
              </Tooltip>
            )}
          </Box>
          <CardThumbnail
            displayImg={displayImg}
            primaryImg={primaryImg}
            onOpen={() => setIsLightboxOpen(true)}
          />
        </Box>
      </Box>
      <Grid>
        {sex && (
          <Cell>
            <PropsHeader>Sex</PropsHeader>
            <PropsValue
              sx={{
                display: 'flex !important',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <QuickFilterRow>
                <Box component="span" sx={{ minWidth: 0, flex: 1 }}>
                  <AnimalSexBadge sex={sex} />
                </Box>
                <QuickFilterButton
                  field={FieldOption.sex.value}
                  filterValue={sex}
                  onClick={handleQuickFilter}
                />
                <AddFilterButton
                  field={FieldOption.sex.value}
                  filterValue={sex}
                  onClick={handleAddFilter}
                />
              </QuickFilterRow>
            </PropsValue>
          </Cell>
        )}
        {identifier && (
          <Cell>
            <PropsHeader>Identifier</PropsHeader>
            <PropsValue sx={{ wordBreak: 'break-all' }}>
              {identifier}
            </PropsValue>
          </Cell>
        )}
        {birthDate && (
          <Cell>
            <PropsHeader>Date of Birth</PropsHeader>
            <PropsValue>
              <SocietyFormattedDate value={birthDate} asDob />
            </PropsValue>
          </Cell>
        )}
        {color && (
          <Cell>
            <PropsHeader>Colour</PropsHeader>
            <PropsValue>{color}</PropsValue>
          </Cell>
        )}
      </Grid>
      {breeder?.id && breeder.value && orgCode && (
        <Footer>
          <PropsHeader>Breeder</PropsHeader>
          <PropsValue>
            <QuickFilterRow>
              <Box component="span" sx={{ minWidth: 0, flex: 1 }}>
                <MemberInlineDisplay
                  memberDisplayName={String(breeder.value)}
                  memberId={Number(breeder.id)}
                  orgName={orgCode}
                  detailsAvailFlag={breeder.detailsAvailFlag}
                  linkStyle={{
                    textDecoration: 'none',
                    lineClamp: 1,
                    WebkitLineClamp: 1,
                    fontSize: isMobile ? '14px' : '16px',
                    lineHeight: isMobile ? '18px' : '20px',
                    paddingInline: 0,
                    minWidth: 0,
                    backgroundColor: 'transparent !important',
                  }}
                />
              </Box>
              <QuickFilterButton
                field={FieldOption.breeder.value}
                filterValue={breeder.value}
                onClick={handleQuickFilter}
              />
              <AddFilterButton
                field={FieldOption.breeder.value}
                filterValue={breeder.value}
                onClick={handleAddFilter}
              />
            </QuickFilterRow>
          </PropsValue>
        </Footer>
      )}
      {owner?.id && orgCode && (
        <Footer>
          <PropsHeader>Owner</PropsHeader>
          <PropsValue>
            <QuickFilterRow>
              <Box component="span" sx={{ minWidth: 0, flex: 1 }}>
                <MemberInlineDisplay
                  memberDisplayName={String(owner.value)}
                  memberId={Number(owner.id)}
                  orgName={orgCode}
                  detailsAvailFlag={owner.detailsAvailFlag}
                  linkStyle={{
                    textDecoration: 'none',
                    lineClamp: 1,
                    WebkitLineClamp: 1,
                    fontSize: isMobile ? '14px' : '16px',
                    lineHeight: isMobile ? '18px' : '20px',
                    paddingInline: 0,
                    minWidth: 0,
                    backgroundColor: 'transparent !important',
                  }}
                />
              </Box>
              <QuickFilterButton
                field={FieldOption.currentOwner.value}
                filterValue={owner.value}
                onClick={handleQuickFilter}
              />
              <AddFilterButton
                field={FieldOption.currentOwner.value}
                filterValue={owner.value}
                onClick={handleAddFilter}
              />
            </QuickFilterRow>
          </PropsValue>
        </Footer>
      )}
      {sire?.id && sire.value && orgCode && (
        <Footer>
          <PropsHeader>Sire</PropsHeader>
          <PropsValue>
            <QuickFilterRow>
              <Box component="span" sx={{ minWidth: 0, flex: 1 }}>
                <Tooltip disableInteractive title="View Details">
                  <PropsLink
                    href={`/${orgCode}/${ROUTE_SEGMENT.ANIMALS}/${sire.id}`}
                  >
                    {sire.value}
                  </PropsLink>
                </Tooltip>
              </Box>
              <QuickFilterButton
                field={FieldOption.sire.value}
                filterValue={sire.value}
                onClick={handleQuickFilter}
              />
              <AddFilterButton
                field={FieldOption.sire.value}
                filterValue={sire.value}
                onClick={handleAddFilter}
              />
            </QuickFilterRow>
          </PropsValue>
        </Footer>
      )}
      {dam?.id && dam.value && orgCode && (
        <Footer>
          <PropsHeader>Dam</PropsHeader>
          <PropsValue>
            <QuickFilterRow>
              <Box component="span" sx={{ minWidth: 0, flex: 1 }}>
                <Tooltip disableInteractive title="View Details">
                  <PropsLink
                    href={`/${orgCode}/${ROUTE_SEGMENT.ANIMALS}/${dam.id}`}
                  >
                    {dam.value}
                  </PropsLink>
                </Tooltip>
              </Box>
              <QuickFilterButton
                field={FieldOption.dam.value}
                filterValue={dam.value}
                onClick={handleQuickFilter}
              />
              <AddFilterButton
                field={FieldOption.dam.value}
                filterValue={dam.value}
                onClick={handleAddFilter}
              />
            </QuickFilterRow>
          </PropsValue>
        </Footer>
      )}
      <ImageLightbox
        open={isLightboxOpen}
        src={displayImg}
        alt={displayName || 'Animal image'}
        title={displayName || undefined}
        onClose={() => setIsLightboxOpen(false)}
      />
    </StyledCard>
  );
}
